import { useRef } from 'react';
import { parseEntity } from '../entity/entity';
import type { VaultEntity } from '../entity/entity';
import { usePlan } from '../plan/usePlan';
import { renderMarkdown } from '../plan/markdown';
import { arcHref } from '../routes/ArcRoute';
import { useCatalog } from '../entity/catalogStore';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { EditableName } from '../design/EditableName';
import { shortDate } from '../design/dates';
import { useNoteText } from '../writing/useNoteText';
import { DocPage } from './DocPage';
import { EditableProse } from './EditableProse';
import { noteDocument, writtenOf } from './doc';
import { arcsHolding, nooksHolding } from './heldBy';

/**
 * A note as a document: its first line is its name, the rest is the column.
 * Both are written where they are read — the name renames in place, and the
 * text turns into its own field in its own type when it is pressed, with no
 * editor around it and no mode to switch into. A note holding more than one
 * piece of content has no one run of text, and is read, not edited.
 */
export function NotePage({ entity }: { entity: VaultEntity }) {
  const { plan } = usePlan();
  const catalog = useCatalog();
  const nooks = useWorkspaces();
  const arcs = plan.status === 'ready' ? plan.plan.arcs : null;
  const field = useRef<HTMLTextAreaElement>(null);
  const { raw, text, structured, edit } = useNoteText(entity.path, field);

  const file = raw ?? entity.raw;
  const doc = noteDocument(entity.path, file);
  const written = writtenOf(parseEntity(entity.path, file));

  // The name is the first line and the column is the rest: each is written
  // back into its own place in the one text.
  const [first = '', ...rest] = text.split('\n');
  const after = rest.join('\n');
  const body = after.replace(/^\n+/, '');
  // The blank lines between the name and the text are the writer's, and stay.
  const gap = `\n${after.slice(0, after.length - body.length) || '\n'}`;
  const lead = first.match(/^#{1,6}\s+/)?.[0] ?? '';
  const setBody = (next: string) => edit(`${first}${gap}${next}`);
  const rename = (name: string) => edit([`${lead}${name}`, ...rest].join('\n'));

  const links = {
    resolveArc: (title: string) => {
      const target = arcs?.find((a) => a.title === title);
      return target ? arcHref(target.name) : null;
    },
  };

  return (
    <DocPage
      fallback="/thoughts"
      tags={catalog.get(entity.path)?.tags ?? []}
      margin={[
        {
          label: 'Held by',
          rows: [
            ...(nooks.status === 'ready' ? nooksHolding(entity.path, nooks.workspaces, catalog) : []),
            ...arcsHolding(entity.path, arcs),
          ],
        },
        {
          label: 'Written',
          rows: written.at
            ? [
                {
                  key: 'written',
                  label: `${written.bySession ? 'by a session, ' : ''}${shortDate(written.at)}`,
                  glyph: { kind: 'none' },
                },
              ]
            : [],
        },
      ]}
    >
      <h1 className="doc-title">
        {structured || raw === null ? doc.title : <EditableName name={doc.title} onRename={rename} />}
      </h1>

      {structured || raw === null ? (
        <div className="doc-body">{renderMarkdown(doc.body, links)}</div>
      ) : (
        <EditableProse text={body} onChange={setBody} links={links} label="Note" />
      )}
    </DocPage>
  );
}
